/**
 * Civil "station" notation helpers (e.g. 99+30, 101+96.62).
 *
 * Inspectors type digits only; `formatStationInput` inserts the "+" before the
 * last two whole-foot digits so "9930" -> "99+30", "10041" -> "100+41",
 * "50" -> "0+50", "10196.62" -> "101+96.62". An explicit "+" is respected.
 */

export function formatStationInput(raw: string): string {
  const s = raw.replace(/[^0-9+.]/g, '');
  if (s === '') return '';
  if (s.includes('+')) {
    const [a = '', rest = ''] = s.split('+', 2);
    const b = rest.replace(/\+/g, '');
    return `${a === '' ? '0' : String(Number(a))}+${b}`;
  }
  const [ip = '', fp] = s.split('.', 2);
  const whole = ip.replace(/^0+(?=\d)/, '');
  let out: string;
  if (whole.length <= 2) out = `0+${whole.padStart(2, '0')}`;
  else out = `${whole.slice(0, -2)}+${whole.slice(-2)}`;
  if (fp !== undefined) out += `.${fp}`;
  return out;
}

/**
 * A station field while it is being typed. Only the digits (and one decimal point) in
 * `text` count: they slide in from the right and the plus places itself before the last
 * two whole feet — 9, 99, 993, 9930 read 0+09, 0+99, 9+93, 99+30. Deleting is the exact
 * reverse (99+30 → 9+93 → 0+99 → 0+09 → empty), so the plus is never typed or deleted by
 * hand. A typed 0 is station 0+00; zeros left over by a deletion are only padding, and the
 * field clears.
 */
export function maskStation(text: string, deleting = false): string {
  const s = text.replace(/[^0-9.]/g, '');
  const dot = s.indexOf('.');
  const frac = dot < 0 ? null : s.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  // leading zeros beyond the three places of 0+00 are padding
  const whole = (dot < 0 ? s : s.slice(0, dot)).replace(/^0+(?=\d{3})/, '').slice(0, 7);
  if (whole === '' && frac === null) return '';
  if (deleting && frac === null && /^0*$/.test(whole)) return '';
  const w = whole.padStart(3, '0');
  return `${w.slice(0, -2)}+${w.slice(-2)}${frac === null ? '' : `.${frac}`}`;
}

/** How many typed characters (digits, the point) lie at or after `caret` in a station field. */
export function stationCharsAfter(text: string, caret: number): number {
  return text.slice(caret).replace(/[^0-9.]/g, '').length;
}

/** The caret position in a masked station that keeps `after` typed characters to its right; next to the plus it sits after it. */
export function stationCaret(masked: string, after: number): number {
  let i = masked.length;
  for (let n = 0; i > 0 && n < after; ) if (masked[--i] !== '+') n++;
  return i;
}

/** Station text -> feet (number) or null when unparsable. */
export function parseStation(text: string): number | null {
  const s = text.trim();
  const m = /^(-?\d+)\+(\d{1,2}(?:\.\d+)?)$/.exec(s);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return a * 100 + Math.sign(a || 1) * b;
  }
  const n = Number(s);
  return Number.isFinite(n) && s !== '' ? n : null;
}

/** Feet -> canonical station text. */
export function stationText(feet: number, decimals = 0): string {
  const sign = feet < 0 ? '-' : '';
  const abs = Math.abs(feet);
  const whole = Math.floor(abs);
  const frac = abs - whole;
  const a = Math.floor(whole / 100);
  const b = whole % 100;
  let out = `${sign}${a}+${String(b).padStart(2, '0')}`;
  if (decimals > 0) out += frac.toFixed(decimals).slice(1);
  return out;
}

export interface CalloutFields {
  template: 'between' | 'at' | 'custom';
  material: string; // display name e.g. "FTB" (already resolved)
  alignment: string;
  from: string;
  to: string;
  custom: string;
  /** Further stretches, each a line of its own: "and between SC1 stations 175+73 and 175+90". */
  ranges?: readonly { alignment: string; from: string; to: string }[];
}

/** Shown where a station has not been typed yet. */
export const STATION_PLACEHOLDER = '\u2014';

/**
 * Compose callout lines. Sorted ascending when both stations parse. A
 * "between" callout with only one station reads as "at" that station, and a
 * missing station shows as a dash so an unfinished callout is obvious.
 */
export function composeCallout(f: CalloutFields): string[] {
  if (f.template === 'custom') return splitLines(f.custom);
  const mat = f.material.trim();
  // stations are kept as typed (digits, mostly) and read as stations here: 9930 → 99+30
  const stretch = (r: { alignment: string; from: string; to: string }, at: boolean): { single: boolean; text: string } => {
    const al = r.alignment.trim();
    const prefix = `${al}${al ? ' ' : ''}`;
    let a = stationAsTyped(r.from),
      b = stationAsTyped(r.to);
    if (at || (a !== '' && b === '') || (a === '' && b !== '')) return { single: true, text: `${prefix}station ${a || b || STATION_PLACEHOLDER}` };
    const fa = parseStation(a),
      fb = parseStation(b);
    if (fa !== null && fb !== null && fa > fb) [a, b] = [b, a];
    return { single: false, text: `${prefix}stations ${a || STATION_PLACEHOLDER} and ${b || STATION_PLACEHOLDER}` };
  };
  const first = stretch(f, f.template === 'at');
  const lines = [`${mat} placed ${first.single ? 'at' : 'between'}`.trim(), first.text];
  // a stretch nobody typed a station into says nothing
  for (const r of f.ranges ?? []) {
    if (r.from.trim() === '' && r.to.trim() === '') continue;
    const more = stretch(r, false);
    lines.push(`and ${more.single ? 'at' : 'between'} ${more.text}`);
  }
  return lines;
}

/** Digits (with an optional decimal part) read as a station; anything else stays as typed. */
export function stationAsTyped(raw: string): string {
  const s = raw.trim().replace(/\.$/, ''); // a point with nothing after it yet
  return /^\d+(\.\d+)?$/.test(s) || /^\d*\+\d+(\.\d+)?$/.test(s) ? formatStationInput(s) : s;
}

/** True when nothing has been entered: such a callout is dropped when deselected. */
export function calloutIsEmpty(c: { template: string; from: string; to: string; custom: string }): boolean {
  if (c.template === 'custom') return c.custom.trim() === '';
  return c.from.trim() === '' && c.to.trim() === '';
}

export function splitLines(text: string): string[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  return lines.length ? lines : [''];
}
